import _ from "lodash";
import { createSelector } from "reselect";
import { Map } from "immutable";

import {
    canPermissionSelectorFactory,
    levelPermissionSelectorFactory,
} from "@auth/selectors";
import { DspsNormalisedEntities } from "@dsps/models";
import { getEntities as getDspsEntities } from "@dsps/selectors";
import { MusicNormalisedEntities } from "@music/models";
import { getEntities as getMusicEntities } from "@music/selectors";
import { makeGetNextPage, makeHasMorePages } from "@orm/selectors";
import { getInitialState } from "../reducers";
import {
    DeliveryState as State,
    Delivery,
    IDelivery,
    Status,
    IStatus,
    Type,
    StatusType,
    DeliveryNormalisedEntities,
} from "../models";

export interface PropsWithId {
    params: {
        id: string;
    };
}

export const getEntities = (state: State) =>
    state.deliveries.entities ? state.deliveries.entities : getInitialState().entities;

export const getPagination = (state: State) =>
    state.deliveries.pagination ? state.deliveries.pagination : null;

export const getList = (state: State) =>
    state.deliveries.list ? state.deliveries.list : [];

export const getFilters = (state: State) =>
    state.deliveries.filters ? state.deliveries.filters : getInitialState().filters;

export const getDeliveries = createSelector(
    getEntities,
    (entities: DeliveryNormalisedEntities) => entities.deliveries,
);

export const getDeliveryTypes = (_state: State) => ([
    new Type({
        name: "insert",
        label: {
            en: "Full New",
            fr: "Livraison",
        },
        description: "A full product delivery of an album or single release metadata, audio files and image file.",
    }),
    new Type({
        name: "full-update",
        label: {
            en: "Full Update",
            fr: "Mise à Jour Complète",
        },
        description: "A full update, where the audio or image files are changing.",
    }),
    new Type({
        name: "full-takedown",
        label: {
            en: "Full Takedown",
            fr: "Takedown",
        },
        description: "A full takedown of album or single.",
    }),
    new Type({
        name: "update-tracks",
        label: {
            en: "Update Tracks",
            fr: "Mise à Jour les Tracks",
        },
        description: "Send an update which contains the new track metadata and audio.",
    }),
    new Type({
        name: "update-metadata",
        label: {
            en: "Update Metadata",
            fr: "Mise à Jour les Métadonnées",
        },
        description: "A metadata only update, without resources files.",
    }),
    new Type({
        name: "update-deal",
        label: {
            en: "Update Deal",
            fr: "Mise à Jour les Contrats",
        },
        description: "An update of the rights deal for album and tracks.",
    }),
]);

export const getDeliveryStatusTypes = (_state: State) => ([
    new StatusType({
        name: "selected",
        label: {
            en: "Selected",
            fr: "Selectionné",
        },
    }),
    new StatusType({
        name: "scheduled",
        label: {
            en: "Scheduled Delivery",
            fr: "Livraison programmée",
        },
    }),
    new StatusType({
        name: "in-progress",
        label: {
            en: "Delivery in progress",
            fr: "Livraison en cours",
        },
    }),
    new StatusType({
        name: "delivered",
        label: {
            en: "Delivered",
            fr: "Livré",
        },
    }),
    new StatusType({
        name: "error",
        label: {
            en: "Error",
            fr: "Erreur",
        },
    }),
    new StatusType({
        name: "cancelled",
        label: {
            en: "Cancelled",
            fr: "Annulé",
        },
    }),
    new StatusType({
        name: "not-available",
        label: {
            en: "Not available",
            fr: "Indisponible",
        },
    }),
    new StatusType({
        name: "available",
        label: {
            en: "Available",
            fr: "Disponible",
        },
    }),
]);

// Pagination selectors
export const hasMorePages = makeHasMorePages(getPagination);
export const getNextPage = makeGetNextPage(getPagination);

// Get individual entities
const getDelivery = ({ deliveries }: State, props: PropsWithId) => {
    const id = props.params.id;
    if (deliveries.entities && deliveries.entities.deliveries.has(id)) {
        const delivery = deliveries.entities.deliveries.get(id);
        if (delivery.exists) {
            return delivery;
        }
        else {
            return null;
        }
    }
    return new Delivery({ id });
};

export const makeGetDelivery = () => createSelector(
    getDelivery,
    getEntities,
    getMusicEntities,
    getDspsEntities,
    (
        item: Delivery,
        entities: DeliveryNormalisedEntities,
        music: MusicNormalisedEntities,
        dsps: DspsNormalisedEntities,
    ) => {
        const hydrate = makeHydrateDelivery(entities, music, dsps);
        return hydrate(item) as Delivery;
    },
);

// Get array of entities
export const getDeliveriesList = createSelector(
    getList,
    getDeliveries,
    getEntities,
    getMusicEntities,
    getDspsEntities,
    (
        list: string[],
        items: Map<string, Delivery>,
        entities: DeliveryNormalisedEntities,
        music: MusicNormalisedEntities,
        dsps: DspsNormalisedEntities,
    ) => {
        const hydrate = makeHydrateDelivery(entities, music, dsps);
        // Only return existing hydrated deliveries.
        return _.reduce(list, (result, id) => {
                if (items.has(id)) {
                    result.push(items.get(id));
                }
                return result;
            }, [] as Delivery[])
            .map((item) => hydrate(item) as Delivery);
    },
);

// Hydrate
export const makeHydrateDelivery =
    (
        entities: DeliveryNormalisedEntities,
        music: MusicNormalisedEntities,
        dsps: DspsNormalisedEntities,
    ) =>
        (e: Delivery | string): Delivery => {
            // if it's an ID, get it from the state.
            if (typeof e === "string") {
                e = entities.deliveries.get(e);
            }

            if (e == null) {
                // Nothing to hydrate
                return e as Delivery;
            }

            const delivery = {} as IDelivery;
            // The batch
            if (e.batch != null &&
                typeof e.batch === "string" &&
                entities.deliveryBatches.has(e.batch)
            ) {
                delivery.batch = entities.deliveryBatches.get(e.batch);
            }
            // The type
            if (e.type != null &&
                typeof e.type === "string" &&
                entities.deliveryTypes.has(e.type)
            ) {
                delivery.type = entities.deliveryTypes.get(e.type);
            }
            // The statuses
            const hydrateStatus = makeHydrateStatus(entities);
            delivery.currentStatus = hydrateStatus(e.currentStatus);
            if (e.statuses != null && e.statuses.length > 0) {
                const statuses = new Array(e.statuses.length);
                for (let i = 0; i < e.statuses.length; i++) {
                    statuses[i] = hydrateStatus(e.statuses[i]) as Status;
                }
                e = e.setCast("statuses", statuses);
            }
            else {
                e = e.setCast("statuses", undefined);
            }
            // The label
            if (e.labelId != null && music.labels.has(e.labelId)) {
                delivery.label = music.labels.get(e.labelId);
            }
            // The album
            if (e.albumId != null && music.albums.has(e.albumId)) {
                delivery.album = music.albums.get(e.albumId).
                    setViewProperties();
            }
            // The dsp
            if (e.dspId != null && dsps.dsps.has(e.dspId)) {
                delivery.dsp = dsps.dsps.get(e.dspId);
            }

            // And the computed data
            e = e.setCast("viewUrl", e.getViewUrl());
            e = e.setCast("hydrated", true);

            return e.merge(delivery) as Delivery;
        };

const makeHydrateStatus =
    (
        entities: DeliveryNormalisedEntities,
    ) =>
        (e: Status | string): Status => {
            // if it's an ID, get it from the state.
            if (typeof e === "string") {
                e = entities.deliveryStatuses.get(e);
            }

            if (e == null) {
                // Nothing to hydrate
                return e as Status;
            }

            const status = {} as IStatus;
            // The StatusType
            if (e.type != null &&
                typeof e.type === "string" &&
                entities.deliveryStatusTypes.has(e.type)
            ) {
                status.type = entities.deliveryStatusTypes.get(e.type);
            }

            return e.merge(status) as Status;
        };

// Permissions selectors
const deliveryPermissionPrefix = "wata.delivery.delivery";
export const canViewDelivery = canPermissionSelectorFactory(deliveryPermissionPrefix, "view");
export const getViewDeliveryLevel = levelPermissionSelectorFactory(deliveryPermissionPrefix, "view");
